import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/token-info`, { params: { token } })
      .then((res) => setInfo(res.data))
      .catch((e) => setError(e.response?.data?.detail || 'This link is invalid or has expired'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/set-password`, { token, password });
      toast.success('Password set. Please sign in.');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally { setSaving(false); }
  };

  const isInvite = info?.type === 'invite';

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background" data-testid="set-password-page">
      <div className="w-full max-w-md space-y-6 animate-fadeIn">
        <div className="text-center">
          <img src="/logo.png" alt="The Merchant - Artisan Coffee & Eatery" className="mx-auto w-56 max-w-full h-auto mb-6" />
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {error ? 'Link expired' : isInvite ? 'Welcome to TMC Stock App' : 'Choose a new password'}
          </h1>
          {info && <p className="text-muted-foreground" data-testid="set-password-email">{info.email}</p>}
        </div>

        {!info && !error && <p className="text-center text-muted-foreground">Checking link...</p>}

        {error && (
          <div className="space-y-4 text-center" data-testid="set-password-error">
            <p className="text-destructive">{error}</p>
            <Link to="/login" className="text-sm text-primary underline" data-testid="back-to-login-link">Back to sign in</Link>
          </div>
        )}

        {info && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="set-password-form">
            <div className="space-y-2">
              <Label htmlFor="new-password">New password</Label>
              <Input id="new-password" type="password" required minLength={6} placeholder="Min. 6 characters"
                value={password} onChange={(e) => setPassword(e.target.value)} data-testid="new-password-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input id="confirm-password" type="password" required minLength={6}
                value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={saving} data-testid="set-password-submit-btn">
              {saving ? 'Saving...' : isInvite ? 'Activate account' : 'Reset password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
